import { useMemo, ElementType, ReactNode } from "react";
import { Box, Button, HStack, Icon, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { FiClock, FiMail, FiMapPin } from "react-icons/fi";
import LinkedInIcon from "components/Icon/Iconify/LinkedInIcon";
import { LuMessageSquare } from "react-icons/lu";
import SideDrawer from "components/SideDrawer";
import ProfileAvatar from "components/ProfileAvatar";
import { getTimezoneDiffHours } from "helpers/time";
import { User } from "features/User/models";
import { RelationType } from "models/v2/networkRelation";

export type SupportPerson = { user: User; roles: RelationType[] };

type ContactButtonProps = { href: string; icon: ElementType; isExternal?: boolean; children: ReactNode };
function ContactButton({ href, icon, isExternal = false, children }: ContactButtonProps) {
  return (
    <Button
      as="a"
      href={href}
      flex={1}
      variant="outline"
      leftIcon={<Icon as={icon} boxSize={4} />}
      size="sm"
      colorScheme="gray"
      color="gray.700"
      borderColor="gray.300"
      fontWeight="500"
      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </Button>
  );
}

function diffLabel(diffHours: number | null): string | null {
  if (diffHours === null || diffHours === 0) return null;
  const abs = Math.abs(diffHours);
  const formatted = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1).replace(".0", "");
  return diffHours > 0
    ? `${formatted} hr${abs !== 1 ? "s" : ""} ahead of you`
    : `${formatted} hr${abs !== 1 ? "s" : ""} behind you`;
}

function PersonDrawerBody({ person }: { person: SupportPerson }) {
  const { user } = person;

  const { currentTimeInTz, timeDiffLabel } = useMemo(() => {
    const diff = user.timezone ? getTimezoneDiffHours(user.timezone) : null;
    return {
      currentTimeInTz: user.timezone
        ? new Intl.DateTimeFormat("en-US", {
            timeZone: user.timezone,
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }).format(new Date())
        : null,
      timeDiffLabel: diffLabel(diff),
    };
  }, [user.timezone]);

  return (
    <VStack spacing={5} align="stretch">
      {/* Header — avatar + name + title */}
      <HStack spacing={3} align="center">
        <ProfileAvatar size="md" user={user} />
        <VStack spacing={0} align="flex-start">
          <CDS.Text16 fontWeight="700" color="gray.900" lineHeight="1.2">
            {user.fullName}
          </CDS.Text16>
          {user.title && (
            <CDS.Text12 color="gray.500">{user.title}</CDS.Text12>
          )}
        </VStack>
      </HStack>

      {/* Location + time — single cream card */}
      {(user.location || currentTimeInTz) && (
        <Box px={4} py={3} bg="bg.canvas" border="1px solid" borderColor="gray.200" borderRadius="md">
          <HStack spacing={0}>
            {user.location && (
              <HStack spacing={1.5} flex={1}>
                <Icon as={FiMapPin} boxSize={3.5} color="gray.400" flexShrink={0} />
                <CDS.Text12 color="gray.600">{user.location}</CDS.Text12>
              </HStack>
            )}
            {currentTimeInTz && (
              <HStack spacing={2}>
                <Icon as={FiClock} boxSize={4} color="gray.500" flexShrink={0} />
                <CDS.Text12 fontWeight="600" color="gray.800">{currentTimeInTz}</CDS.Text12>
                {timeDiffLabel && (
                  <CDS.Text12 color="gray.400">· {timeDiffLabel}</CDS.Text12>
                )}
              </HStack>
            )}
          </HStack>
        </Box>
      )}

      {/* Bio */}
      {user.bio && (
        <CDS.Text12 color="gray.600" lineHeight="1.7">{user.bio}</CDS.Text12>
      )}

      {/* Action buttons */}
      {(user.slackUrl || user.workEmail || user.linkedinUrl) && (
        <HStack spacing={2} pt={1}>
          {user.slackUrl && (
            <ContactButton href={user.slackUrl} icon={LuMessageSquare} isExternal>Message</ContactButton>
          )}
          {user.workEmail && (
            <ContactButton href={`mailto:${user.workEmail}`} icon={FiMail}>Email</ContactButton>
          )}
          {user.linkedinUrl && (
            <ContactButton href={user.linkedinUrl} icon={LinkedInIcon} isExternal>LinkedIn</ContactButton>
          )}
        </HStack>
      )}
    </VStack>
  );
}

interface PersonDrawerProps {
  person: SupportPerson | null;
  onClose: () => void;
}

export default function PersonDrawer({ person, onClose }: PersonDrawerProps) {
  return (
    <SideDrawer<SupportPerson>
      isOpen={!!person}
      onClose={onClose}
      item={person ?? undefined}
      size="sm"
    >
      {({ item }) => (item ? <PersonDrawerBody person={item} /> : null)}
    </SideDrawer>
  );
}
